/**
 * Space theme tokens.
 *
 * A Space theme is a small, strict-hex palette that recolours one Space's
 * sidebar, channels, and home. It is applied by setting the regular UI theme
 * CSS variables on the Space's wrapper element, so every existing component
 * inside the Space picks it up without knowing about Space themes.
 *
 * Tokens are optional: an unset token leaves the viewer's own theme value in
 * place for that slot.
 *
 * @module types/space-theme
 */
import { hexToRgbTriplet, hexWithAlpha, isHexColor, mixHex, shadeHex, } from '../utils/color';
/** All Space theme token keys, in panel display order. */
export const SPACE_THEME_TOKEN_KEYS = [
    'background',
    'sidebar',
    'card',
    'elevated',
    'text',
    'textMuted',
    'accent',
    'accentText',
    'link',
    'border',
];
/** Panel grouping for the Space theme editor. */
export const SPACE_THEME_TOKEN_GROUPS = {
    surfaces: ['background', 'sidebar', 'card', 'elevated'],
    text: ['text', 'textMuted'],
    accents: ['accent', 'accentText', 'link', 'border'],
};
/** Upper bound on a serialized Space theme (JSON) — used to cap ciphertext too. */
export const SPACE_THEME_JSON_MAX_LENGTH = 1024;
/** A member's account-wide default for themed Spaces. */
export const SPACE_THEME_DEFAULTS = ['space', 'own'];
export const DEFAULT_SPACE_THEME_DEFAULT = 'space';
/**
 * Whether a member sees a Space's theme.
 *
 * A saved per-Space choice (`useSpaceTheme`) wins; otherwise the account-wide
 * default applies. A missing preference document counts as "no choice".
 */
export function resolveUseSpaceTheme(params) {
    if (typeof params.useSpaceTheme === 'boolean')
        return params.useSpaceTheme;
    return (params.spaceThemeDefault ?? DEFAULT_SPACE_THEME_DEFAULT) === 'space';
}
/** Whether a theme sets at least one token. */
export function hasSpaceTheme(theme) {
    if (!theme)
        return false;
    return SPACE_THEME_TOKEN_KEYS.some((key) => isHexColor(theme[key]));
}
/**
 * Keep only known keys with strict hex values, lowercased. Anything else is
 * dropped. Used on decrypted themes (whose contents the server never saw) and
 * as a last line of defence before values reach inline styles.
 */
export function sanitizeSpaceTheme(raw) {
    const out = {};
    if (!raw || typeof raw !== 'object')
        return out;
    const record = raw;
    for (const key of SPACE_THEME_TOKEN_KEYS) {
        const value = record[key];
        if (isHexColor(value))
            out[key] = value.toLowerCase();
    }
    return out;
}
/**
 * Convert Space theme tokens into CSS custom properties.
 *
 * Emits the direct variable for each set token, the derived shades that the
 * full UI themes hand-author (hover, active, glow, muted), and the `-rgb`
 * triplets some components read. Invalid values are ignored. Output keys are
 * sorted so the result is deterministic.
 */
export function spaceThemeToCssVars(tokens) {
    const t = sanitizeSpaceTheme(tokens);
    const vars = {};
    if (t.background) {
        vars['--color-bg-primary'] = t.background;
    }
    if (t.sidebar) {
        vars['--color-bg-secondary'] = t.sidebar;
        vars['--color-bg-secondary-rgb'] = hexToRgbTriplet(t.sidebar);
    }
    if (t.card) {
        vars['--color-bg-tertiary'] = t.card;
        vars['--color-bg-tertiary-rgb'] = hexToRgbTriplet(t.card);
        vars['--color-bg-hover'] = shadeHex(t.card, 0.06);
        vars['--color-bg-active'] = shadeHex(t.card, 0.12);
    }
    if (t.elevated) {
        vars['--color-bg-elevated'] = t.elevated;
        vars['--color-bg-elevated-rgb'] = hexToRgbTriplet(t.elevated);
    }
    if (t.text) {
        vars['--color-text-primary'] = t.text;
        vars['--color-text-primary-rgb'] = hexToRgbTriplet(t.text);
    }
    if (t.textMuted) {
        vars['--color-text-secondary'] = t.textMuted;
        vars['--color-text-muted'] = t.background
            ? mixHex(t.textMuted, t.background, 0.25)
            : shadeHex(t.textMuted, 0.15);
    }
    if (t.accent) {
        vars['--color-accent-primary'] = t.accent;
        vars['--color-accent-primary-rgb'] = hexToRgbTriplet(t.accent);
        vars['--color-accent-primary-hover'] = shadeHex(t.accent, 0.12);
        vars['--color-accent-primary-active'] = shadeHex(t.accent, 0.24);
        vars['--color-accent-secondary'] = shadeHex(t.accent, 0.12);
        vars['--color-accent-glow'] = hexWithAlpha(t.accent, 0.15);
        vars['--color-border-focus'] = t.accent;
    }
    if (t.accentText) {
        vars['--color-text-inverse'] = t.accentText;
    }
    if (t.link) {
        vars['--color-link'] = t.link;
    }
    if (t.border) {
        vars['--color-border'] = t.border;
        vars['--color-border-muted'] = t.background
            ? mixHex(t.border, t.background, 0.5)
            : shadeHex(t.border, 0.2);
    }
    const sorted = {};
    for (const key of Object.keys(vars).sort())
        sorted[key] = vars[key];
    return sorted;
}
//# sourceMappingURL=space-theme.js.map